import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";

import DetailsPageHeader from "../../../../common/DetailsPageHeader";
import Sidebar from "../../../../common/Sidebar";
import TableHeadComponent from "../../../../common/TableHeadComponent";
import TablePaginationSection from "../../../../common/TablePaginationSection";
import { TableRowSection } from "../../components/TableRowSection/TableRowSection";
import {
  EmailTemplatesTableHeadCells,
  getComparator,
  mainLayoutStyles,
  routes,
  showNotification,
  stableSort,
} from "../../../../constants";
import { useAuth } from "../../../../hooks";
import { useOrganizationContext } from "../../../../providers/OrganizationAdmin/OrganizationProvider";
import {
  EMAIL_TEMPLATE_SCOPE_GEN,
  useAddStandardTemplatesToOrg,
  useGetEmailTemplates,
} from "../../hooks/EmailTemplatesHooks";

// Only active standard templates are shown, and they have no default flag,
// so the Status and Default columns are hidden.
const tableHeadCells = EmailTemplatesTableHeadCells.filter(
  (c) => c.id !== "default" && c.id !== "status"
);

/**
 * Org Admin: browse active standard email templates (genEmailTemplates) and
 * copy one or more into the organization's templates (orgEmailTemplates).
 */
const StandardEmailTemplatesContainer = () => {
  const navigate = useNavigate();
  const { currentUser, organizationId } = useAuth();
  const { currentOrganization } = useOrganizationContext() || {};
  const userId = currentUser?.id;

  const { templates, loading } = useGetEmailTemplates(null, {
    scope: EMAIL_TEMPLATE_SCOPE_GEN,
    activeOnly: true,
  });
  const { addStandardTemplatesToOrg, loading: addLoading } =
    useAddStandardTemplatesToOrg();

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");

  const filteredTemplates = useMemo(() => {
    if (!searchText) return templates;
    const lowerSearch = searchText.toLowerCase();
    return templates.filter(
      (t) =>
        t.name?.toLowerCase().includes(lowerSearch) ||
        t.description?.toLowerCase().includes(lowerSearch)
    );
  }, [templates, searchText]);

  const sortedTemplates = useMemo(
    () => stableSort(filteredTemplates, getComparator(order, orderBy)),
    [filteredTemplates, order, orderBy]
  );

  const paginatedTemplates = useMemo(
    () =>
      sortedTemplates.slice(
        (page - 1) * rowsPerPage,
        (page - 1) * rowsPerPage + rowsPerPage
      ),
    [sortedTemplates, page, rowsPerPage]
  );

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(paginatedTemplates.map((n) => n.id));
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleRowClick = (templateId) => {
    navigate(`/email-templates/standard/details/${templateId}`);
  };

  const handleSearch = (text) => {
    setSearchText(text);
    setPage(1);
  };

  const handleAddToOrg = async () => {
    if (selected.length === 0) {
      showNotification("Please select at least one template");
      return;
    }

    const result = await addStandardTemplatesToOrg(
      selected,
      organizationId,
      userId,
      currentOrganization?.logoOP
    );

    if (result.success) {
      showNotification(
        `Successfully added ${result.count} template(s) to your email templates`
      );
      setSelected([]);
      navigate(routes.emailTemplates);
    } else {
      showNotification("Error adding templates");
    }
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  return (
    <Box sx={{ display: "flex", width: "100%", paddingRight: 4 }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={"Add Standard Templates"}
        bgColor="#fff"
        onBackClick={() => navigate(routes.emailTemplates)}
      />
      <Sidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} sx={{ mt: 5 }}>
          {/* Search + bulk action */}
          <Grid container alignItems={"center"} style={{ marginBottom: 10 }}>
            <TextField
              variant="outlined"
              placeholder={"Search templates..."}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                sx: { ...searchTextfieldStyles },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />

            {selected.length > 0 && (
              <>
                <span style={selectedItemsStyles}>
                  {selected.length} selected
                </span>
                <TrendingFlatIcon fontSize="large" style={{ color: "#242424" }} />
                <span style={actionTextStyles} onClick={handleAddToOrg}>
                  Add to My Templates
                </span>
              </>
            )}
          </Grid>

          <Box sx={{ height: 6 }} />

          <Paper sx={{ width: "100%", mb: 2, mx: 2 }}>
            <TableContainer>
              <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
                <TableHeadComponent
                  TableHeadData={tableHeadCells}
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={paginatedTemplates.length}
                />
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : paginatedTemplates.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{
                          py: 5,
                          fontFamily: "Avenir Book, sans-serif",
                          color: "#595959",
                        }}
                      >
                        {searchText
                          ? "No templates found matching your search"
                          : "No standard templates available."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedTemplates.map((row, index) => (
                      <TableRowSection
                        key={row.id}
                        row={row}
                        isItemSelected={isSelected(row.id)}
                        labelId={`enhanced-table-checkbox-${index}`}
                        handleClick={handleClick}
                        handleRowClick={handleRowClick}
                        showDefault={false}
                        showStatus={false}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePaginationSection
              count={Math.ceil(filteredTemplates.length / rowsPerPage)}
              rowsPerPage={rowsPerPage}
              page={page}
              total={filteredTemplates.length ?? 0}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(1);
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || addLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default StandardEmailTemplatesContainer;

// Match OrganizationEmailTemplates SearchBar styling
const searchTextfieldStyles = {
  height: 40,
  border: "none",
  borderColor: "#7676801F",
  borderRadius: 5,
  width: 270,
  background: "#7676801F",
  color: "#515563",
  fontSize: 14,
  fontWeight: 500,
  paddingLeft: 1,
  paddingRight: 2,
  fontFamily: "Avenir Book, sans-serif",
};

const selectedItemsStyles = {
  color: "#020303",
  marginLeft: 10,
  marginRight: 10,
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "Avenir Book, sans-serif",
};

const actionTextStyles = {
  color: "#00A7CD",
  marginLeft: 10,
  marginRight: 10,
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "Avenir Book, sans-serif",
};
